import type { HTMLAttributes, ReactNode } from "react";
import { tv, type VariantProps } from "tailwind-variants";

import { cn } from "@/lib/utils";

import { Heading, Text } from "@/components/base/Text";

const card = tv({
  base: "bg-card rounded-xl border border-border shadow-card",
  variants: {
    padding: {
      none: "p-0",
      sm: "p-3",
      md: "p-5",
      lg: "p-6",
    },
  },
  defaultVariants: {
    padding: "md",
  },
});

type CardProps = HTMLAttributes<HTMLDivElement> & VariantProps<typeof card>;

export const Card: React.FC<CardProps> = ({
  className,
  padding,
  children,
  ...props
}) => {
  return (
    <div className={card({ padding, className })} {...props}>
      {children}
    </div>
  );
};

type CardHeaderProps = {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
};

export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  description,
  action,
  className,
}) => {
  return (
    <div className={cn("flex items-start justify-between gap-4", className)}>
      <div className="flex flex-col gap-1">
        <Heading as="h3" variant="headingMd">
          {title}
        </Heading>
        {description && (
          <Text variant="bodyMd" tone="subdued">
            {description}
          </Text>
        )}
      </div>
      {action && (
        <div className="flex shrink-0 items-center gap-2">{action}</div>
      )}
    </div>
  );
};

type SectionProps = HTMLAttributes<HTMLDivElement>;

export const CardSection: React.FC<SectionProps> = ({
  className,
  children,
  ...props
}) => {
  return (
    <div
      className={cn(
        "border-border border-t px-5 py-4 first:border-t-0 first:pt-0 last:pb-0",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};
